import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  MessageCircle,
  Send,
  Facebook,
  Instagram,
  Twitter
} from "lucide-react";
import contactHeroBg from "@/assets/contact-hero-bg.jpg";
import { useSiteConfig } from "@/hooks/useSiteConfig";

const Contact = () => {
  const { getConfig } = useSiteConfig();
  
  const contactInfo = [
    {
      icon: Phone,
      title: "Phone Number",
      details: [getConfig('contact_phone', '+1 (809) 123-4567')],
      description: getConfig('contact_phone_description', "Available 24/7 for bookings and emergencies")
    },
    {
      icon: Mail,
      title: "Email Address",
      details: [getConfig('contact_email', 'info@traveltourcjm.com')],
      description: getConfig('contact_email_description', "We respond within 2 hours during business hours")
    },
    {
      icon: MapPin,
      title: "Location",
      details: [getConfig('contact_location', 'Punta Cana')],
      description: getConfig('contact_location_description', "Serving all of Punta Cana and surrounding areas")
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: ["24/7 Service Available"],
      description: "Emergency and airport transfers available anytime"
    }
  ];

  const serviceAreas = [
    "Punta Cana",
    "Bavaro",
    "Cap Cana",
    "Uvero Alto",
    "La Romana",
    "Santo Domingo",
    "Samaná",
    "Puerto Plata"
  ];

  const socialLinks = [
    { 
      icon: Facebook, 
      name: "Facebook", 
      url: getConfig('social_facebook')?.url || '#',
      enabled: getConfig('social_facebook')?.enabled || false
    },
    { 
      icon: Instagram, 
      name: "Instagram", 
      url: getConfig('social_instagram')?.url || '#',
      enabled: getConfig('social_instagram')?.enabled || false
    },
    { 
      icon: Twitter, 
      name: "TikTok", 
      url: getConfig('social_tiktok')?.url || '#',
      enabled: getConfig('social_tiktok')?.enabled || false
    }
  ].filter(social => social.enabled);

  return (
    <Layout>
      {/* Hero Section */}
      <section 
        className="py-24 text-white relative bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{ backgroundImage: `url(${contactHeroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-accent/60"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <Badge className="mb-4 bg-secondary text-white border-0 shadow-secondary hover:bg-secondary/90">{getConfig('contact_hero_badge', 'Contact Us')}</Badge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 drop-shadow-lg">
              {getConfig('contact_hero_title', "Let's Connect")}
            </h1>
            <p className="text-xl md:text-2xl text-white/95 leading-relaxed drop-shadow">
              {getConfig('contact_hero_description', 'Ready to experience the best of Dominican Republic? Contact Dominican Sol Transfers for premium transportation and unforgettable tours.')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <Badge className="mb-4 bg-accent text-white border-0">{getConfig('contact_info_badge', 'Contact Information')}</Badge>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
              {getConfig('contact_info_title', "We're Here to Help")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {getConfig('contact_info_description', "Choose the most convenient way to contact us. We're committed to responding quickly and providing all the information you need.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <Card key={index} className="text-center hover:shadow-primary transition-all duration-300 hover:-translate-y-2 border-0 shadow-md">
                <CardHeader>
                  <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mb-4">
                    <info.icon className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">{info.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {info.details.map((detail, detailIndex) => (
                      <div key={detailIndex} className="font-medium text-primary">
                        {detail}
                      </div>
                    ))}
                  </div>
                  <CardDescription className="text-base">
                    {info.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-gradient-secondary">{getConfig('contact_form_badge', 'Send Us a Message')}</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {getConfig('contact_form_title', 'Quick Contact Form')}
              </h2>
              <p className="text-lg text-muted-foreground">
                {getConfig('contact_form_description', "Fill out the form below and we'll get back to you within 2 hours during business hours")}
              </p>
            </div>

            <Card className="border-0 shadow-primary">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Book or Inquire</CardTitle>
                <CardDescription className="text-center text-base">
                  Whether you want to book a service or just have questions, we're here to help
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input id="firstName" placeholder="Your first name" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input id="lastName" placeholder="Your last name" required />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input id="email" type="email" placeholder="your.email@example.com" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input id="phone" placeholder="+1 (xxx) xxx-xxxx" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="service">Service Type *</Label>
                      <select 
                        id="service" 
                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        required
                      >
                        <option value="">Select a service</option>
                        <option value="airport-transfer">Airport Transfer</option>
                        <option value="private-tour">Private Tour</option>
                        <option value="group-transport">Group Transportation</option>
                        <option value="custom-package">Custom Package</option>
                        <option value="general-inquiry">General Inquiry</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date">Preferred Date</Label>
                      <Input id="date" type="date" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="guests">Number of Guests</Label>
                    <select 
                      id="guests" 
                      className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                    >
                      <option value="">Select number of guests</option>
                      <option value="1">1 Guest</option>
                      <option value="2">2 Guests</option>
                      <option value="3-4">3-4 Guests</option>
                      <option value="5-8">5-8 Guests</option>
                      <option value="9-15">9-15 Guests</option>
                      <option value="16+">16+ Guests</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Please provide details about your request, including pickup location, destination, special requirements, or any questions you have..."
                      rows={5}
                      required
                    />
                  </div>

                  <Button className="w-full bg-gradient-primary shadow-primary hover:shadow-secondary transition-all duration-300 hover:scale-105 text-lg py-6">
                    <Send className="mr-2 h-5 w-5" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-primary">{getConfig('contact_areas_badge', 'Service Areas')}</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {getConfig('contact_areas_title', 'Where We Operate')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {getConfig('contact_areas_description', 'We provide services throughout the Dominican Republic, with special focus on the most popular tourist destinations')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {serviceAreas.map((area, index) => (
              <Card key={index} className="text-center p-4 hover:shadow-primary transition-all duration-300 hover:-translate-y-1 border-0 shadow-sm">
                <div className="text-primary font-medium">{area}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-primary bg-gradient-hero text-white">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <Phone className="h-8 w-8" />
                </div>
                <CardTitle className="text-3xl mb-4">{getConfig('contact_emergency_title', '24/7 Emergency Line')}</CardTitle>
                <CardDescription className="text-white/90 text-lg">
                  {getConfig('contact_emergency_description', 'For urgent assistance, emergency transportation, or last-minute bookings')}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-4xl font-bold mb-4">{getConfig('contact_phone', '+1 (809) 123-4567')}</div>
                <p className="text-white/90 mb-6">
                  {getConfig('contact_emergency_text', 'Available 24 hours a day, 7 days a week for emergency situations, airport transfers, and urgent transportation needs')}
                </p>
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Media */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-secondary">{getConfig('contact_social_badge', 'Follow Us')}</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {getConfig('contact_social_title', 'Stay Connected')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {getConfig('contact_social_description', 'Follow us on social media for travel tips, destination highlights, and special offers')}
            </p>
          </div>

          <div className="flex justify-center space-x-6">
            {socialLinks.map((social, index) => (
              <a 
                key={index}
                href={social.url}
                className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center text-white hover:bg-gradient-secondary transition-all duration-300 hover:scale-110 hover:shadow-primary"
              >
                <social.icon className="h-8 w-8" />
              </a>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;